import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDriversService from '../../../services/useDriverService';
import useServicesService from '../../../services/useServicesService';
import { Driver } from '../../../models/DriverModel';
import { Service } from '../../../models/ServiceModel';
import { ServiceStatus } from '../../../enums/ServiceEnum';
import { canAccessDriverEditPage } from '../../../hooks/usePermissions';

const SERVICE_STATUSES = [
  ServiceStatus.ASIGNADO,
  ServiceStatus.EN_CURSO,
  ServiceStatus.COMPLETADO,
].join(',');

type ServiceEntry = {
  service: Service;
  dt: Date;
};

type DriverRow = {
  driverId: number;
  driverName: string;
  plate?: string;
  entries: ServiceEntry[];
  hasUpcoming: boolean;
  nextUpcoming: ServiceEntry | null;
};

const toDateTime = (s: Service): Date | null => {
  if (!s.service_date || !s.service_time) return null;
  const time = String(s.service_time).trim().slice(0, 8);
  const dt = new Date(`${s.service_date}T${time}`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

const formatClock = (dt: Date) =>
  `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;

const formatDay = (dt: Date) =>
  `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}`;

const isoDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfDay = (d: Date) => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};

const endOfDay = (d: Date) => {
  const out = new Date(d);
  out.setHours(23, 59, 59, 999);
  return out;
};

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const statusMeta = (status?: ServiceStatus | string) => {
  switch (status) {
    case ServiceStatus.ASIGNADO:
      return { label: 'Asignado', tone: 'asignado' };
    case ServiceStatus.EN_CURSO:
      return { label: 'En curso', tone: 'curso' };
    case ServiceStatus.COMPLETADO:
      return { label: 'Completado', tone: 'completado' };
    default:
      return { label: status || '—', tone: 'default' };
  }
};

const routeLabel = (s: Service) =>
  `${s.origin_name || '—'} → ${s.destination_name || '—'}`;

const DriverServicesSearch: React.FC = () => {
  const navigate = useNavigate();
  const { list: listDrivers } = useDriversService();
  const { list: listServices } = useServicesService();

  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<DriverRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [rangeFrom, setRangeFrom] = useState<Date | null>(null);
  const [rangeTo, setRangeTo] = useState<Date | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const infoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!infoOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (!infoRef.current?.contains(event.target as Node)) {
        setInfoOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setInfoOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [infoOpen]);

  const loadAll = () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const nowMs = Date.now();
    const today = new Date(nowMs);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const from = startOfDay(yesterday);
    const to = endOfDay(today);
    setRangeFrom(from);
    setRangeTo(to);
    setLoading(true);

    let driversAcc: Driver[] = [];
    let servicesAcc: Service[] = [];
    let driversDone = false;
    let servicesDone = false;

    const finishIfReady = () => {
      if (!driversDone || !servicesDone || controller.signal.aborted) return;

      const byDriver = new Map<number, Service[]>();
      servicesAcc.forEach((s) => {
        const driverId = Number(s.driver);
        if (!Number.isFinite(driverId) || driverId <= 0) return;
        const list = byDriver.get(driverId) ?? [];
        list.push(s);
        byDriver.set(driverId, list);
      });

      const fromMs = from.getTime();
      const toMs = to.getTime();

      const built: DriverRow[] = driversAcc
        .flatMap((d): DriverRow[] => {
          const driverId = Number(d.id);
          if (!Number.isFinite(driverId) || driverId <= 0) return [];
          const theirs = byDriver.get(driverId) ?? [];
          const entries: ServiceEntry[] = theirs
            .map((s) => {
              const dt = toDateTime(s);
              if (!(dt instanceof Date)) return null;
              const t = dt.getTime();
              if (t < fromMs || t > toMs) return null;
              return { service: s, dt };
            })
            .filter((e): e is ServiceEntry => e !== null)
            .sort((a, b) => a.dt.getTime() - b.dt.getTime());
          const nextUpcoming = entries.find((e) => e.dt.getTime() > nowMs) ?? null;
          const plate = d.vehicle_license_plate || undefined;
          return [
            {
              driverId,
              driverName:
                `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim() ||
                `Conductor #${driverId}`,
              ...(plate ? { plate } : {}),
              entries,
              hasUpcoming: nextUpcoming !== null,
              nextUpcoming,
            },
          ];
        })
        .sort((a, b) => {
          if (a.hasUpcoming !== b.hasUpcoming) return a.hasUpcoming ? -1 : 1;
          const aHas = a.entries.length > 0;
          const bHas = b.entries.length > 0;
          if (aHas !== bHas) return aHas ? -1 : 1;
          const aFirst = a.entries[0]?.dt.getTime() ?? Infinity;
          const bFirst = b.entries[0]?.dt.getTime() ?? Infinity;
          if (aFirst !== bFirst) return aFirst - bFirst;
          return a.driverName.localeCompare(b.driverName, 'es');
        });

      setRows(built);
      setLoading(false);
    };

    const loadDriversPage = (page: number) => {
      listDrivers(
        { page, page_size: 200, sort_field: 'first_name', sort_order: 'asc' },
        {
          onSuccess: (resp: any) => {
            if (controller.signal.aborted) return;
            const data = resp?.data ?? {};
            const items: Driver[] = Array.isArray(data.drivers) ? data.drivers : data.items ?? [];
            driversAcc = [...driversAcc, ...items];
            if (data.pagination?.has_next) {
              loadDriversPage(page + 1);
              return;
            }
            driversDone = true;
            finishIfReady();
          },
          onError: () => {
            if (controller.signal.aborted) return;
            driversDone = true;
            finishIfReady();
          },
        },
        controller.signal
      );
    };

    const loadServicesPage = (page: number) => {
      listServices(
        {
          status: SERVICE_STATUSES,
          start_date: isoDate(from),
          end_date: isoDate(to),
          general_mode: 1,
          page,
          page_size: 100,
        },
        {
          onSuccess: (resp: any) => {
            if (controller.signal.aborted) return;
            const data = resp?.data ?? {};
            const items: Service[] = Array.isArray(data.services) ? data.services : data.items ?? [];
            servicesAcc = [...servicesAcc, ...items];
            if (data.pagination?.has_next) {
              loadServicesPage(page + 1);
              return;
            }
            servicesDone = true;
            finishIfReady();
          },
          onError: () => {
            if (controller.signal.aborted) return;
            servicesDone = true;
            finishIfReady();
          },
        },
        controller.signal
      );
    };

    loadDriversPage(1);
    loadServicesPage(1);
  };

  useEffect(() => {
    loadAll();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = normalize(query);
    if (!term) return rows;
    return rows.filter((row) => {
      const driver = normalize(row.driverName);
      const plate = normalize(row.plate ?? '');
      if (driver.includes(term) || plate.includes(term)) return true;
      return row.entries.some((e) => {
        const company = normalize(String(e.service.company_name ?? ''));
        const origin = normalize(String(e.service.origin_name ?? ''));
        const destination = normalize(String(e.service.destination_name ?? ''));
        return company.includes(term) || origin.includes(term) || destination.includes(term);
      });
    });
  }, [rows, query]);

  const upcomingDriversCount = useMemo(
    () => filtered.filter((row) => row.hasUpcoming).length,
    [filtered]
  );

  const rangeLabel =
    rangeFrom && rangeTo ? `${formatDay(rangeFrom)} → ${formatDay(rangeTo)}` : '—';

  const passengersLabel = (s: Service) => {
    if (Array.isArray(s.passenger_details) && s.passenger_details.length > 0) {
      return s.passenger_details.map((p) => p.name).filter(Boolean).join(', ') || '—';
    }
    if (s.passenger && String(s.passenger).trim() !== '') return String(s.passenger);
    if (Array.isArray(s.passengers) && s.passengers.length > 0) {
      return `${s.passengers.length} pasajero${s.passengers.length === 1 ? '' : 's'}`;
    }
    return '—';
  };

  const dayGroupsFor = (row: DriverRow, dayFrom: Date, dayTo: Date) => {
    const fromKey = isoDate(dayFrom);
    const toKey = isoDate(dayTo);
    const ayer = row.entries.filter((e) => isoDate(e.dt) === fromKey);
    const hoy = row.entries.filter((e) => isoDate(e.dt) === toKey);
    const groups: Array<{ label: string; entries: ServiceEntry[] }> = [];
    if (ayer.length > 0) groups.push({ label: 'Ayer', entries: ayer });
    if (hoy.length > 0) groups.push({ label: 'Hoy', entries: hoy });
    return groups;
  };

  const goToDriver = (driverId: number) => {
    if (canAccessDriverEditPage()) {
      navigate(`/drivers/edit/${driverId}`);
    } else {
      navigate('/drivers');
    }
  };

  return (
    <section className="coord-panel coord-panel--compact coord-driver-search" aria-label="Buscar conductor">
      <div className="coord-panel-head-inline">
        <h2 className="coord-panel-title">Buscar conductor</h2>
        <div className="coord-driver-search-info" ref={infoRef}>
          <button
            type="button"
            className={`coord-driver-search-info-btn${infoOpen ? ' is-open' : ''}`}
            aria-label="Cómo funciona el buscador"
            aria-expanded={infoOpen}
            aria-controls="coord-driver-search-info-panel"
            onClick={() => setInfoOpen((open) => !open)}
            title="Cómo funciona el buscador"
          >
            <span aria-hidden="true">i</span>
          </button>
          {infoOpen && (
            <div
              id="coord-driver-search-info-panel"
              className="coord-driver-search-info-panel"
              role="dialog"
              aria-label="Cómo funciona el buscador"
            >
              <p className="coord-driver-search-info-title">Cómo funciona</p>
              <ul>
                <li>Se listan <strong>todos los conductores</strong>.</li>
                <li>
                  El rango cubre <strong>el día anterior y el día actual</strong> (48 horas)
                  {rangeFrom && rangeTo ? ` (${rangeLabel})` : ''}.
                </li>
                <li>
                  Se muestran <strong>todos los servicios asignados</strong> del conductor en ese
                  rango, agrupados por día, con su hora.
                </li>
                <li>
                  Si alguno de esos servicios aún no comienza, se marca como{' '}
                  <strong>Próximo</strong> — el conductor ya tiene trabajo para las próximas horas.
                </li>
                <li>Escribe en el buscador para filtrar por nombre, patente o empresa.</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="coord-driver-search-range" aria-label="Rango de búsqueda">
        <span className="coord-driver-search-range-label">Rango</span>
        <span className="coord-driver-search-range-value">{rangeLabel}</span>
        <span className="coord-driver-search-range-note">Día anterior + hoy · 48 h</span>
      </div>

      <div className="coord-driver-search-field">
        <span className="coord-driver-search-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <input
          type="search"
          className="coord-driver-search-input"
          value={query}
          placeholder="Filtrar por conductor…"
          autoComplete="off"
          onChange={(event) => setQuery(event.target.value)}
        />
        {query && (
          <button
            type="button"
            className="coord-driver-search-clear"
            onClick={() => setQuery('')}
            aria-label="Limpiar búsqueda"
          >
            ×
          </button>
        )}
      </div>

      <div className="coord-driver-search-meta">
        <span>
          {loading
            ? 'Cargando…'
            : `${filtered.length} conductor${filtered.length === 1 ? '' : 'es'}${
                upcomingDriversCount > 0 ? ` · ${upcomingDriversCount} con próximo servicio` : ''
              }`}
        </span>
      </div>

      <div className="coord-driver-search-results" aria-live="polite">
        {loading ? (
          <p className="coord-driver-search-empty">Cargando conductores…</p>
        ) : filtered.length === 0 ? (
          <p className="coord-driver-search-empty">
            {query.trim() ? 'Sin coincidencias para esa búsqueda.' : 'Sin conductores.'}
          </p>
        ) : (
          <ul className="coord-driver-service-list">
            {filtered.map((row) => {
              const groups = rangeFrom && rangeTo ? dayGroupsFor(row, rangeFrom, rangeTo) : [];
              return (
                <li key={row.driverId}>
                  <div
                    className={`coord-driver-service-card${
                      row.hasUpcoming ? ' is-upcoming' : row.entries.length > 0 ? ' is-has' : ' is-empty'
                    }`}
                  >
                    <div className="coord-driver-service-head">
                      <span className="coord-driver-service-driver">{row.driverName}</span>
                      {row.plate ? (
                        <span className="coord-driver-service-company">{row.plate}</span>
                      ) : null}
                      {row.nextUpcoming ? (
                        <span className="coord-driver-service-next">
                          Próximo · {formatClock(row.nextUpcoming.dt)}
                        </span>
                      ) : null}
                    </div>

                    {row.entries.length === 0 ? (
                      <button
                        type="button"
                        className="coord-driver-service-empty-link"
                        onClick={() => goToDriver(row.driverId)}
                      >
                        Sin servicios en el rango
                      </button>
                    ) : (
                      groups.map((group) => (
                        <div key={group.label} className="coord-driver-service-daygroup">
                          <span className="coord-driver-service-daylabel">{group.label}</span>
                          <div className="coord-driver-service-chips">
                            {group.entries.map((entry) => {
                              const meta = statusMeta(entry.service.status);
                              const isUpcoming = row.nextUpcoming?.service.id === entry.service.id;
                              return (
                                <button
                                  key={entry.service.id ?? `${row.driverId}-${entry.dt.getTime()}`}
                                  type="button"
                                  className={`coord-driver-service-chip is-${meta.tone}${
                                    isUpcoming ? ' is-upcoming' : ''
                                  }`}
                                  title={`${entry.service.company_name || `Empresa ${entry.service.company}`} · ${routeLabel(
                                    entry.service
                                  )} · ${passengersLabel(entry.service)} · ${meta.label}`}
                                  onClick={() => {
                                    if (entry.service.id) navigate(`/services/edit/${entry.service.id}`);
                                  }}
                                >
                                  {formatClock(entry.dt)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

export default DriverServicesSearch;
